import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import dayjs, { Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";
import CustomDatePicker from "../customDatePicker/customDatePicker.component";

export type ContractEndDateDuration = "1m" | "6m" | "1y" | "indefinite";

const DURATION_MONTHS: Record<Exclude<ContractEndDateDuration, "indefinite">, number> = {
  "1m": 1,
  "6m": 6,
  "1y": 12,
};

export function calculateContractEndDate(
  duration: ContractEndDateDuration
): Dayjs | null {
  if (duration === "indefinite") {
    return null;
  }
  return dayjs().add(DURATION_MONTHS[duration], "month").endOf("month").startOf("day");
}

interface ContractEndDatePickerProps {
  /** `null` means "indefinite" (no end date); `undefined` means "not chosen yet" — only shows the indefinite hint for `null`. */
  value: Dayjs | null | undefined;
  onChange: (value: Dayjs | null) => void;
  label?: string;
  required?: boolean;
  size?: "small" | "medium";
  sx?: object;
}

export default function ContractEndDatePicker({
  value,
  onChange,
  label,
  required,
  size = "small",
  sx,
}: ContractEndDatePickerProps) {
  const { t } = useTranslation();
  const isIndefinite = value === null;

  const durationOptions: { value: ContractEndDateDuration; label: string }[] = [
    { value: "1m", label: t("1 month") },
    { value: "6m", label: t("6 months") },
    { value: "1y", label: t("1 year") },
    { value: "indefinite", label: t("Indefinitely") },
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, ...sx }}>
      <CustomDatePicker
        label={label ?? t("End date")}
        value={value ?? null}
        onChange={onChange}
        slotProps={{
          textField: {
            size,
            fullWidth: true,
            required,
          },
        }}
      />
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {durationOptions.map((option) => (
          <Button
            key={option.value}
            size="small"
            variant={
              option.value === "indefinite" && isIndefinite ? "contained" : "outlined"
            }
            onClick={() => onChange(calculateContractEndDate(option.value))}
          >
            {option.label}
          </Button>
        ))}
      </Stack>
    </Box>
  );
}
